import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaGithub,
  FaExternalLinkAlt,
  FaArrowLeft,
  FaLinkedin,
  FaLink,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaArrowRight,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import projectsData from '../data/projects.json';
import '../styles/ProjectDetails.css';

interface Collaborator {
  name: string;
  role?: string;
  linkedin?: string;
}

type CollaboratorInput = Collaborator | string;

interface ResourceLink {
  label: string;
  url: string;
}

interface Project {
  id: string;
  title: string;
  thumbnail: string;
  heroImage?: string;
  description: string;
  longDescription?: string | string[];
  tags: string[];
  github?: string;
  liveUrl?: string;
  month?: string;
  year?: string;
  endMonth?: string;
  endYear?: string;
  featured?: boolean;
  images?: string[];
  videos?: string[];
  contributions?: string[];
  collaborators?: CollaboratorInput[];
  resourceLinks?: ResourceLink[];
}

const COLLAPSED_DESCRIPTION_HEIGHT = 280;

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const project = projectsData.find((p) => p.id === id) as Project | undefined;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [canExpandDescription, setCanExpandDescription] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  const galleryImages = project?.images ?? [];
  const isLightboxOpen = lightboxIndex !== null && lightboxIndex < galleryImages.length;

  useEffect(() => {
    setIsDescriptionExpanded(false);
    setLightboxIndex(null);
  }, [id]);

  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxIndex(null);
      } else if (event.key === 'ArrowRight') {
        setLightboxIndex((prev) =>
          prev === null ? null : (prev + 1) % galleryImages.length
        );
      } else if (event.key === 'ArrowLeft') {
        setLightboxIndex((prev) =>
          prev === null ? null : (prev - 1 + galleryImages.length) % galleryImages.length
        );
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLightboxOpen, galleryImages.length]);

  useEffect(() => {
    const descriptionEl = descriptionRef.current;
    if (!descriptionEl) return;

    const checkOverflow = () => {
      setCanExpandDescription(descriptionEl.scrollHeight > COLLAPSED_DESCRIPTION_HEIGHT + 8);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(descriptionEl);

    return () => resizeObserver.disconnect();
  }, [id, project?.longDescription, project?.description]);

  // Convert collaborator input to Collaborator object
  const normalizeCollaborator = (input: CollaboratorInput): Collaborator => {
    if (typeof input === 'string') {
      // Extract name from LinkedIn URL
      const match = input.match(/linkedin\.com\/in\/([^/?]+)/);
      const username = match ? match[1] : 'Collaborator';
      // Convert username to readable name (replace hyphens with spaces and capitalize)
      const name = username
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return {
        name,
        linkedin: input
      };
    }
    return input;
  };

  // Helper to format date range for display
  const formatDateRange = (month?: string, year?: string, endMonth?: string, endYear?: string): string => {
    const hasStart = month || year;
    const hasEnd = endMonth || endYear;
    
    if (!hasStart && !hasEnd) return 'Recent Project';
    
    const startDate = month && year ? `${month} ${year}` : year || month || '';
    
    if (!hasEnd) return startDate;
    
    const endDate = endMonth && endYear ? `${endMonth} ${endYear}` : endYear || endMonth || '';
    
    // If same month and year, just show one date
    if (startDate === endDate) return startDate;
    
    // If same year but different months
    if (year === endYear && month && endMonth) {
      return `${month} - ${endMonth} ${year}`;
    }
    
    return `${startDate} - ${endDate}`;
  };

  // Convert video URL to embed URL
  const getEmbedUrl = (videoUrl: string) => {
    if (!videoUrl) return '';
    
    // Handle YouTube URLs
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      let videoId = '';
      let startTime = '';
      
      // Extract timestamp if present (t=180 or t=3m30s format)
      const timeMatch = videoUrl.match(/[?&]t=(\d+)/);
      if (timeMatch) {
        startTime = timeMatch[1];
      }
      // Also handle t=3m30s format
      const timeMatch2 = videoUrl.match(/[?&]t=(\d+)m(\d+)s/);
      if (timeMatch2) {
        startTime = String(parseInt(timeMatch2[1]) * 60 + parseInt(timeMatch2[2]));
      }
      
      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1]?.split('&')[0];
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      } else if (videoUrl.includes('youtube.com/embed/')) {
        videoId = videoUrl.split('embed/')[1]?.split('?')[0];
      }
      
      if (videoId) {
        let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=0&loop=1&playlist=${videoId}`;
        if (startTime) {
          embedUrl += `&start=${startTime}`;
        }
        return embedUrl;
      }
    }
    
    // Handle Vimeo URLs
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0]?.split('#')[0];
      // Extract timestamp for Vimeo (#t=180s format)
      const vimeoTimeMatch = videoUrl.match(/#t=(\d+)s/);
      let embedUrl = `https://player.vimeo.com/video/${videoId}?loop=1`;
      if (vimeoTimeMatch) {
        embedUrl += `#t=${vimeoTimeMatch[1]}s`;
      }
      return embedUrl;
    }
    
    // If it's already an embed URL, return as is
    if (videoUrl.includes('/embed/') || videoUrl.startsWith('https://player.')) {
      return videoUrl;
    }
    
    // For direct video files (mp4, webm, etc.), return the URL
    if (videoUrl.match(/\.(mp4|webm|ogg)$/i)) {
      return videoUrl;
    }
    
    return videoUrl; // Return original if no pattern matches
  };

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const showPrevImage = () => {
    setLightboxIndex((prev) =>
      prev === null ? null : (prev - 1 + galleryImages.length) % galleryImages.length
    );
  };
  const showNextImage = () => {
    setLightboxIndex((prev) =>
      prev === null ? null : (prev + 1) % galleryImages.length
    );
  };

  return (
    <div className="project-details-page">
      <Navbar />

      <section className="project-details-section">
        <div className="container">
          <motion.div
            className="back-link-wrapper"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/projects" className="back-link">
              <FaArrowLeft /> Back to Projects
            </Link>
          </motion.div>

          <motion.div
            className="project-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="project-header-content">
              <div className="project-title-section">
                {project.featured && <span className="featured-badge">Featured</span>}
                <h1 className="project-title">{project.title}</h1>
                <p className="project-date">
                  {formatDateRange(project.month, project.year, project.endMonth, project.endYear)}
                </p>
              </div>

              <div className="project-links">
                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link-btn"
                  >
                    <FaGithub /> View Code
                  </a>
                )}
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-link-btn primary"
                  >
                    <FaExternalLinkAlt /> Live Demo
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="project-image"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}${project.heroImage || project.thumbnail}`}
              alt={project.title}
              onError={(e) => {
                const svg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='600'%3E%3Crect fill='%232a2a30' width='1200' height='600'/%3E%3Ctext fill='%232a81b9' font-family='Arial' font-size='48' font-weight='bold' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3E${encodeURIComponent(project.title)}%3C/text%3E%3C/svg%3E`;
                e.currentTarget.src = svg;
                e.currentTarget.onerror = null;
              }}
            />
          </motion.div>

          <motion.div
            className="project-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="project-main">
              <h2>About This Project</h2>
              <div className="project-description-block">
                <div className="project-description-clipper">
                  <div
                    ref={descriptionRef}
                    id="project-description"
                    className={`project-description ${
                      isDescriptionExpanded || !canExpandDescription ? 'expanded' : 'collapsed'
                    }`}
                  >
                    {Array.isArray(project.longDescription) ? (
                      project.longDescription.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))
                    ) : (
                      <p>{project.longDescription || project.description}</p>
                    )}
                  </div>
                  {canExpandDescription && !isDescriptionExpanded && (
                    <div className="project-description-fade" aria-hidden="true" />
                  )}
                </div>
                {canExpandDescription && (
                  <button
                    type="button"
                    className="project-description-toggle"
                    onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                    aria-expanded={isDescriptionExpanded}
                    aria-controls="project-description"
                  >
                    {isDescriptionExpanded ? (
                      <>
                        Show less <FaChevronUp />
                      </>
                    ) : (
                      <>
                        Read more <FaChevronDown />
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Project Videos */}
              {project.videos && project.videos.length > 0 && (
                <>
                  <h3>Video Demonstrations</h3>
                  <div className="project-videos">
                    {project.videos.map((videoUrl, index) => {
                      const embedUrl = getEmbedUrl(videoUrl);
                      const isDirectVideo = videoUrl.match(/\.(mp4|webm|ogg)$/i);
                      
                      return (
                        <div key={index} className="video-wrapper">
                          {isDirectVideo ? (
                            <video controls loop muted>
                              <source src={`${process.env.PUBLIC_URL}${videoUrl}`} type={`video/${videoUrl.split('.').pop()}`} />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <iframe
                              src={embedUrl}
                              title={`${project.title} video ${index + 1}`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Project Images */}
              {project.images && project.images.length > 0 && (
                <>
                  <h3>Project Gallery</h3>
                  <div className="project-gallery">
                    {project.images.map((image, index) => (
                      <button
                        key={index}
                        type="button"
                        className="gallery-item"
                        onClick={() => openLightbox(index)}
                        aria-label={`View ${project.title} image ${index + 1} full size`}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}${image}`}
                          alt={`${project.title} screenshot ${index + 1}`}
                          onError={(e) => {
                            const svg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400'%3E%3Crect fill='%232a2a30' width='600' height='400'/%3E%3Ctext fill='%232a81b9' font-family='Arial' font-size='24' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EImage ${index + 1}%3C/text%3E%3C/svg%3E`;
                            e.currentTarget.src = svg;
                            e.currentTarget.onerror = null;
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="project-sidebar">
              <div className="project-info-card">
                <h3>Technologies Used</h3>
                <div className="project-tags">
                  {project.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {(project.github || project.liveUrl || (project.resourceLinks && project.resourceLinks.length > 0)) && (
                <div className="project-info-card">
                  <h3>Project Links</h3>
                  <div className="project-external-links">
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        <FaGithub /> GitHub Repository
                      </a>
                    )}
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        <FaExternalLinkAlt /> Live Demo
                      </a>
                    )}
                    {project.resourceLinks && project.resourceLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        <FaLink /> {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="project-info-card">
                <h3>Project Date</h3>
                <p>{formatDateRange(project.month, project.year, project.endMonth, project.endYear)}</p>
              </div>

              {project.contributions && project.contributions.length > 0 && (
                <div className="project-info-card">
                  <h3>My Contributions</h3>
                  <ul className="sidebar-contributions">
                    {project.contributions.map((contribution, index) => (
                      <li key={index}>{contribution}</li>
                    ))}
                  </ul>
                </div>
              )}

              {project.collaborators && project.collaborators.length > 0 && (
                <div className="project-info-card">
                  <h3>Collaborators</h3>
                  <div className="project-external-links">
                    {project.collaborators.map((collabInput, index) => {
                      const collab = normalizeCollaborator(collabInput);
                      const label = (
                        <span className="sidebar-collaborator-label">
                          <span className="sidebar-collaborator-name">{collab.name}</span>
                          {collab.role && (
                            <span className="sidebar-collaborator-role">{collab.role}</span>
                          )}
                        </span>
                      );

                      return collab.linkedin ? (
                        <a
                          key={index}
                          href={collab.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link sidebar-collaborator-link"
                        >
                          <FaLinkedin /> {label}
                        </a>
                      ) : (
                        <div key={index} className="sidebar-collaborator-entry">
                          {label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="other-projects-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2>Explore More Projects</h2>
            <Link to="/projects" className="view-all-btn">
              View All Projects <FaArrowLeft style={{ transform: 'rotate(180deg)' }} />
            </Link>
          </motion.div>
        </div>
      </section>

      {isLightboxOpen && (
        <div
          className="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${project.title} image gallery`}
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="Close image viewer"
          >
            <FaTimes />
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox-nav lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrevImage();
                }}
                aria-label="Previous image"
              >
                <FaArrowLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="lightbox-nav lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  showNextImage();
                }}
                aria-label="Next image"
              >
                <FaArrowRight aria-hidden="true" />
              </button>
            </>
          )}

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${process.env.PUBLIC_URL}${galleryImages[lightboxIndex!]}`}
              alt={`${project.title} screenshot ${lightboxIndex! + 1}`}
            />
            {galleryImages.length > 1 && (
              <p className="lightbox-counter">
                {lightboxIndex! + 1} / {galleryImages.length}
              </p>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectDetails;

